import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import logo from '../assets/uplink-logo.svg';
import { type Lang, usePrefs } from '../prefs.tsx';

export type AuthMode = 'signin' | 'signup' | 'forgot' | 'reset';

const AUTH_TEXT = {
  ru: {
    pitchTitle: 'Один аккаунт в браузере и Telegram',
    pitchText: 'Привяжите Telegram в профиле — баланс, домены и серверы будут одинаковыми везде.',
    payments: 'Оплата криптовалютой через @CryptoBot и @xRocket',
    account: 'Аккаунт',
    signIn: 'Войти',
    signUp: 'Регистрация',
    titles: {
      signin: 'С возвращением',
      signup: 'Создайте аккаунт',
      forgot: 'Восстановите пароль',
      reset: 'Задайте новый пароль',
    },
    domain: (domain: string) => <>Создайте аккаунт, чтобы зарегистрировать <b className="ul-land__mono">{domain}</b>.</>,
    resetSent: (email: string) => `Если аккаунт с адресом ${email} существует, мы отправили ссылку для смены пароля. Она действует один час.`,
    name: 'Имя',
    email: 'Email',
    password: 'Пароль',
    newPassword: 'Новый пароль',
    forgotPassword: 'Забыли пароль?',
    passwordHint: 'Минимум 8 символов.',
    busy: 'Минуту…',
    actions: { signin: 'Войти', signup: 'Создать аккаунт', forgot: 'Отправить ссылку', reset: 'Сохранить и войти' },
    or: 'или',
    localAdmin: 'Войти как локальный администратор',
    signingIn: 'Входим…',
    confirmTelegram: 'Подтвердите вход в Telegram.',
    reopenBot: 'Снова открыть бота',
    continueTelegram: 'Продолжить через Telegram',
    devHint: 'Режим разработки: production-бот не имеет доступа к локальной базе.',
    telegramHint: 'Откроется бот: подтвердите вход одним нажатием.',
    back: 'Вернуться ко входу',
  },
  en: {
    pitchTitle: 'One account on the web and in Telegram',
    pitchText: 'Link Telegram in your profile, and your balance, domains and servers are the same everywhere.',
    payments: 'Payments in crypto via @CryptoBot and @xRocket', account: 'Account', signIn: 'Sign in', signUp: 'Sign up',
    titles: { signin: 'Welcome back', signup: 'Create your account', forgot: 'Reset your password', reset: 'Set a new password' },
    domain: (domain: string) => <>Create an account to register <b className="ul-land__mono">{domain}</b>.</>,
    resetSent: (email: string) => `If an account uses ${email}, a link to set a new password is on its way. It works for an hour.`,
    name: 'Name', email: 'Email', password: 'Password', newPassword: 'New password', forgotPassword: 'Forgot password?',
    passwordHint: 'At least 8 characters.', busy: 'One moment…',
    actions: { signin: 'Sign in', signup: 'Create account', forgot: 'Send the link', reset: 'Save and sign in' },
    or: 'or', localAdmin: 'Continue as local administrator', signingIn: 'Signing in…',
    confirmTelegram: 'Confirm the sign-in in Telegram.', reopenBot: 'Open the bot again',
    continueTelegram: 'Continue with Telegram',
    devHint: 'Development mode: the production bot cannot access your local database.',
    telegramHint: "The bot opens: confirm with one tap and you're in.", back: 'Back to sign in',
  },
} satisfies Record<Lang, Record<string, unknown>>;

function Field({
  id,
  label,
  type = 'text',
  value,
  onChange,
  autoComplete,
  aside,
}: {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'password';
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  aside?: React.ReactNode;
}) {
  return (
    <div className="ul-auth__field">
      <div className="ul-auth__label">
        <label htmlFor={id}>{label}</label>
        {aside}
      </div>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
      />
    </div>
  );
}

/**
 * Sign in, sign up, a forgotten password and a new one from the emailed link, plus signing in through the
 * bot. The website is in English; a successful sign-in refetches everything and the app takes over.
 */
export function AuthPage({
  mode,
  setMode,
  resetToken,
  pendingDomain,
  onHome,
}: {
  mode: AuthMode;
  setMode: (m: AuthMode) => void;
  resetToken: string | null;
  pendingDomain: string | null;
  onHome: () => void;
}) {
  const trpc = useTRPC();
  const { lang } = usePrefs();
  const text = AUTH_TEXT[lang];
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [sent, setSent] = useState(false);
  const [tg, setTg] = useState<{ token: string; link: string } | null>(null);
  const signedIn = () => queryClient.invalidateQueries();

  const login = useMutation(trpc.auth.login.mutationOptions({ onSuccess: signedIn }));
  const devLogin = useMutation(trpc.auth.devLogin.mutationOptions({ onSuccess: signedIn }));
  const register = useMutation(trpc.auth.register.mutationOptions({ onSuccess: signedIn }));
  const forgot = useMutation(trpc.auth.requestReset.mutationOptions({ onSuccess: () => setSent(true) }));
  const reset = useMutation(trpc.auth.resetPassword.mutationOptions({ onSuccess: signedIn }));
  const tgStart = useMutation(
    trpc.auth.telegramStart.mutationOptions({
      onSuccess: (r) => {
        setTg(r);
        window.open(r.link, '_blank', 'noopener');
      },
    }),
  );
  const tgPoll = useMutation(trpc.auth.telegramPoll.mutationOptions());

  // Waiting for the bot: ask every two seconds until the person confirms or the link runs out
  const polling = useRef(false);
  useEffect(() => {
    if (!tg) return;
    const timer = setInterval(async () => {
      if (polling.current) return;
      polling.current = true;
      try {
        const r = await tgPoll.mutateAsync({ token: tg.token });
        if (r.status === 'approved') {
          clearInterval(timer);
          await signedIn();
        } else if (r.status === 'expired') {
          clearInterval(timer);
          setTg(null);
        }
      } catch {
        // a missed poll is retried on the next tick
      } finally {
        polling.current = false;
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [tg]);

  const busy = login.isPending || devLogin.isPending || register.isPending || forgot.isPending || reset.isPending;
  const error =
    (mode === 'signin' && login.error) ||
    (mode === 'signup' && register.error) ||
    (mode === 'forgot' && forgot.error) ||
    (mode === 'reset' && reset.error) ||
    tgStart.error ||
    devLogin.error;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'signin') login.mutate({ email, password });
    else if (mode === 'signup')
      register.mutate({ email, password, name: name.trim() || undefined, lang });
    else if (mode === 'forgot') forgot.mutate({ email, lang });
    else if (resetToken) reset.mutate({ token: resetToken, password });
  };

  return (
    <div className="ul-auth">
      <section className="ul-auth__panel">
        <button type="button" className="ul-auth__brand" onClick={onHome}>
          <img src={logo} alt="" width={44} height={44} />
          Uplink
        </button>
        <div className="ul-auth__pitch">
          <h1>{text.pitchTitle}</h1>
          <p>{text.pitchText}</p>
        </div>
        <span className="ul-auth__small">{text.payments}</span>
      </section>

      <section className="ul-auth__main">
        <form className="ul-auth__form" onSubmit={submit}>
          {mode === 'signin' || mode === 'signup' ? (
            <div className="ul-auth__tabs" role="tablist" aria-label={text.account}>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signin'}
                onClick={() => setMode('signin')}
              >
                {text.signIn}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signup'}
                onClick={() => setMode('signup')}
              >
                {text.signUp}
              </button>
            </div>
          ) : null}

          <h2>{text.titles[mode]}</h2>
          {pendingDomain && mode === 'signup' ? (
            <p className="ul-auth__note">
              {text.domain(pendingDomain)}
            </p>
          ) : null}

          {mode === 'forgot' && sent ? (
            <p className="ul-auth__note">
              {text.resetSent(email)}
            </p>
          ) : (
            <>
              {mode === 'signup' ? (
                <Field id="auth-name" label={text.name} value={name} onChange={setName} autoComplete="name" />
              ) : null}
              {mode !== 'reset' ? (
                <Field
                  id="auth-email"
                  label={text.email}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="email"
                />
              ) : null}
              {mode !== 'forgot' ? (
                <Field
                  id="auth-password"
                  label={mode === 'reset' ? text.newPassword : text.password}
                  type="password"
                  value={password}
                  onChange={setPassword}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  aside={
                    mode === 'signin' ? (
                      <button type="button" className="ul-auth__link" onClick={() => setMode('forgot')}>
                        {text.forgotPassword}
                      </button>
                    ) : null
                  }
                />
              ) : null}
              {mode === 'signup' || mode === 'reset' ? (
                <p className="ul-auth__hint">{text.passwordHint}</p>
              ) : null}

              {error ? (
                <div className="ul-auth__error" role="alert">
                  {errorText(error)}
                </div>
              ) : null}

              <button type="submit" className="ul-land__btn ul-auth__submit" disabled={busy}>
                {busy
                  ? text.busy
                  : text.actions[mode]}
              </button>
            </>
          )}

          {mode === 'signin' || mode === 'signup' ? (
            <>
              <div className="ul-auth__or">
                <span>{text.or}</span>
              </div>
              {import.meta.env.DEV ? (
                <button
                  type="button"
                  className="ul-land__btn ul-land__btn--ghost ul-auth__submit"
                  disabled={devLogin.isPending}
                  onClick={() => devLogin.mutate({})}
                >
                  {devLogin.isPending
                    ? text.signingIn
                    : text.localAdmin}
                </button>
              ) : tg ? (
                <div className="ul-auth__waiting" aria-live="polite">
                  <span className="ul-spinner" aria-hidden />
                  <span>
                    {text.confirmTelegram}{' '}
                    <a href={tg.link} target="_blank" rel="noopener noreferrer">
                      {text.reopenBot}
                    </a>
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="ul-land__btn ul-land__btn--ghost ul-auth__submit"
                  disabled={tgStart.isPending}
                  onClick={() => tgStart.mutate()}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m22 2-7 20-4-9-9-4z" />
                    <path d="M22 2 11 13" />
                  </svg>
                  {text.continueTelegram}
                </button>
              )}
              <p className="ul-auth__hint ul-auth__center">
                {import.meta.env.DEV
                  ? text.devHint
                  : text.telegramHint}
              </p>
            </>
          ) : (
            <button type="button" className="ul-auth__link ul-auth__center" onClick={() => setMode('signin')}>
              {text.back}
            </button>
          )}
        </form>
      </section>
    </div>
  );
}
